import { ensureIdentity } from '@client/util/account-sdk';

import * as actions from './actions';
import {
    SCHIBSTED_ACCOUNT_START,
    SCHIBSTED_ACCOUNT_INIT,
    SCHIBSTED_ACCOUNT_HAS_SESSION,
    SCHIBSTED_ACCOUNT_HAS_SESSION_COMPLETE,
    SCHIBSTED_ACCOUNT_HAS_SESSION_ERROR,
    SCHIBSTED_ACCOUNT_SUBSCRIBE_EVENTS,
    SCHIBSTED_ACCOUNT_EVENT_SESSION_ERROR,
    SCHIBSTED_ACCOUNT_LOGIN,
    SCHIBSTED_ACCOUNT_LOGOUT,
} from './types';

let identity;

export const login = (options = {}) => identity.login(options);

// config
let logoutRedirectUri;

export default ({ dispatch }) => next => async (action) => {
    const nextAction = next(action);

    if (action.type === SCHIBSTED_ACCOUNT_START) {
        return dispatch(actions.init(action.payload))
            .then(() => dispatch(actions.subscribeEvents()))
            .then(() => dispatch(actions.hasSession()));
    }

    if (action.type === SCHIBSTED_ACCOUNT_INIT) {
        identity = ensureIdentity(action.payload);

        if (action.payload.enableVarnishCookie) {
            const varnishCookieOptions = {};
            if (action.payload.varnishCookieDomain) {
                varnishCookieOptions.domain = action.payload.varnishCookieDomain;
            }
            identity.enableVarnishCookie(varnishCookieOptions);
        }

        if (action.payload.logoutRedirectUri) {
            // eslint-disable-next-line prefer-destructuring
            logoutRedirectUri = action.payload.logoutRedirectUri;
        }

        dispatch(actions.initComplete({
            identity,
        }));

        return Promise.resolve({
            identity,
        });
    }

    if (action.type === SCHIBSTED_ACCOUNT_SUBSCRIBE_EVENTS) {
        if (!identity) {
            return Promise.reject(new Error('Schibsted Account Identity not initialized'));
        }

        identity.on('notLoggedin', data => dispatch(actions.eventNotLoggedin(data)));
        identity.on('statusChange', data => dispatch(actions.eventStatusChange(data)));
        identity.on('login', data => dispatch(actions.eventLogin(data)));
        identity.on('logout', data => dispatch(actions.eventLogout(data)));
        identity.on('userChange', data => dispatch(actions.eventUserChange(data)));
        identity.on('sessionInit', data => dispatch(actions.eventSessionInit(data)));
        identity.on('sessionChange', data => dispatch(actions.eventSessionChange(data)));
        identity.on('visitor', data => dispatch(actions.eventVisitor(data)));
        identity.on('error', data => dispatch(actions.eventSessionError(data)));

        return Promise.resolve();
    }

    if (action.type === SCHIBSTED_ACCOUNT_HAS_SESSION) {
        if (!identity) {
            return Promise.reject(new Error('Schibsted Account Identity not initialized'));
        }

        return identity.hasSession()
            .then((response) => {
                dispatch(actions.hasSessionComplete(response));
                return response;
            },
            (err) => {
                dispatch(actions.hasSessionError(err));
            });
    }

    if (action.type === SCHIBSTED_ACCOUNT_HAS_SESSION_COMPLETE || action.type === SCHIBSTED_ACCOUNT_HAS_SESSION_ERROR) {
        if (!identity) {
            return Promise.reject(new Error('Schibsted Account Identity not initialized'));
        }

        if (action.type === SCHIBSTED_ACCOUNT_HAS_SESSION_COMPLETE) {
            dispatch(actions.generateUrls({
                logout: identity.logout,
                login,
                accountUrl: identity.accountUrl(),
            }));
        } else if (action.type === SCHIBSTED_ACCOUNT_HAS_SESSION_ERROR) {
            dispatch(actions.generateUrls({
                login,
            }));
        }

        return Promise.resolve();
    }

    if (action.type === SCHIBSTED_ACCOUNT_LOGIN) {
        if (!identity) {
            return Promise.reject(new Error('Schibsted Account Identity not initialized'));
        }

        return login(action.payload);
    }

    if (action.type === SCHIBSTED_ACCOUNT_LOGOUT) {
        if (!identity) {
            return Promise.reject(new Error('Schibsted Account Identity not initialized'));
        }

        const {
            redirectUri = logoutRedirectUri || window.location.href,
        } = action.payload || {};
        return identity.logout(redirectUri);
    }

    if (action.type === SCHIBSTED_ACCOUNT_EVENT_SESSION_ERROR) {
        if (!identity) {
            return Promise.reject(new Error('Schibsted Account Identity not initialized'));
        }

        return identity.clearCachedUserSession();
    }

    return nextAction;
};
